import { useCallback, useEffect, useState } from "react";
import { useSubAssemblyService } from "../../../../services/subAssemblyService";
import { SubAssembly } from "../../../../types/subAssembly";
import { useSnackbar } from "../../../../contexts/snackbar-context";

export function useSubAssemblies() {
  const [subAssemblies, setSubAssemblies] = useState<SubAssembly[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    subAssembly: SubAssembly | null;
  }>({ open: false, subAssembly: null });

  const { showSuccess, showError } = useSnackbar();
  const {
    getSubAssemblies,
    getSubAssembly,
    createSubAssembly,
    updateSubAssembly,
    deleteSubAssembly,
  } = useSubAssemblyService();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await getSubAssemblies();
      setSubAssemblies(items);
    } catch {
      showError("Failed to load sub-assemblies");
    } finally {
      setLoading(false);
    }
  }, [getSubAssemblies, showError]);

  const save = useCallback(
    async (data: Partial<SubAssembly>, id?: string) => {
      try {
        let installDate = data.installDate?.trim() ? data.installDate : null;
        if (
          installDate &&
          typeof installDate === "string" &&
          !installDate.includes("T")
        ) {
          installDate = new Date(installDate).toISOString();
        }

        const payload = {
          landingGearType: data.landingGearType ?? "",
          subAssemblyType: data.subAssemblyType ?? "",
          serialNumber: data.serialNumber ?? "",
          partNumber: data.partNumber?.trim() ? data.partNumber : null,
          installDate,
          offWingType: data.offWingType ?? null,
          shopVisitReasonId: data.shopVisitReasonId || null,
          airframeFamily: data.airframeFamily?.trim() ? data.airframeFamily : null,
        };

        if (!payload.landingGearType || !payload.subAssemblyType || !payload.serialNumber) {
          showError("Landing gear type, sub-assembly type, and serial number are required");
          return;
        }

        if (!payload.airframeFamily) {
          showError("Airframe type is required");
          return;
        }

        if (payload.offWingType === "In Shop" && !payload.shopVisitReasonId) {
          showError("Inshop reason is required when OffWing Type is In Shop");
          return;
        }

        if (id) {
          await updateSubAssembly(id, payload);
        } else {
          await createSubAssembly(payload);
        }

        showSuccess(`Sub-assembly ${id ? "updated" : "created"} successfully`);
        await load();
      } catch {
        showError(`Failed to ${id ? "update" : "create"} sub-assembly`);
      }
    },
    [createSubAssembly, updateSubAssembly, showSuccess, showError, load]
  );

  const remove = useCallback(async (subAssembly: SubAssembly) => {
    setConfirmDelete({ open: true, subAssembly });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const subAssembly = confirmDelete.subAssembly;
    if (!subAssembly?.id) return;

    setIsDeleting(true);
    try {
      await deleteSubAssembly(subAssembly.id);
      showSuccess("Sub-assembly deleted successfully");
      setConfirmDelete({ open: false, subAssembly: null });
      await load();
    } catch {
      showError("Failed to delete sub-assembly");
      setConfirmDelete({ open: false, subAssembly: null });
    } finally {
      setIsDeleting(false);
    }
  }, [confirmDelete.subAssembly, deleteSubAssembly, showSuccess, showError, load]);

  const fetchById = useCallback(
    async (id: string): Promise<SubAssembly | null> => {
      try {
        return await getSubAssembly(id);
      } catch {
        showError("Failed to fetch sub-assembly details");
        return null;
      }
    },
    [getSubAssembly, showError]
  );

  useEffect(() => {
    void load();
  }, [load]);

  return {
    subAssemblies,
    loading,
    save,
    remove,
    fetchById,
    confirmDelete,
    isDeleting,
    setConfirmDelete,
    handleConfirmDelete,
  };
}
